import { useState } from 'react'

import { fehlertext } from '../../api/client'
import { useMedienExport, useSettings } from '../../hooks/queries'
import { alsApp } from '../../lib/betriebsart'
import { groesse } from '../../lib/format'
import { notiere } from '../../lib/sendeprotokoll'
import { sichereDatei } from '../../lib/sichern'
import { useTexte } from '../../texte'

/**
 * "Medien als ZIP" (#75): the server packs the photos, videos and voice
 * messages of the trip and hands back a link, like the book. The link
 * opens the file the same way as the book's link: in the app started from
 * the home screen, Safari opens and the file can be saved there.
 *
 * **And since 1.53.0 a button "ZIP sichern"** next to it -- the same way as
 * the book: fetched into the page, then the share sheet on iPhone and iPad
 * ("In Dateien sichern"), a download elsewhere. Its own tap is the gesture
 * the sheet needs (`brauchtEigeneGeste()` in `lib/sichern.ts`); after the
 * packing the tap on "Medien als ZIP" is long gone.
 *
 * **Here stood "not fetched into the page"**, with the reason that a ZIP of
 * a trip can be gigabytes and a fetch of that size can end the tab. The
 * reason still holds; the decision from use is to try anyway, without a
 * limit -- and to leave the link standing for the case it goes wrong.
 *
 * The places in the file names come from Apple Maps (the second exception
 * in `CLAUDE.md`) -- said here, in the sentence under the button, like on
 * every search that leaves this server.
 */
export function MedienExport({ tripId }: { tripId: number }) {
  const t = useTexte().buch
  const build = useMedienExport(tripId)
  const einstellungen = useSettings()
  const ortsnamen = einstellungen.data?.services?.place === true
  const [built, setBuilt] = useState<{ url: string; filename: string; bytes: number; files: number } | null>(null)
  const [holt, setHolt] = useState(false)
  const [hinweis, setHinweis] = useState<{ text: string; fehler: boolean } | null>(null)

  /** The second gesture: fetch the ZIP and hand it over (sheet or download). */
  async function sichern(ziel: { url: string; filename: string; bytes: number }) {
    setHolt(true)
    setHinweis(null)
    const started = performance.now()
    try {
      const way = await sichereDatei(`${ziel.url}?name=${encodeURIComponent(ziel.filename)}`, ziel.filename, 'application/zip')
      notiere(`Medien: ZIP ${way} (${groesse(ziel.bytes)}) nach ${((performance.now() - started) / 1000).toFixed(1)} s`)
      if (way === 'geladen') setHinweis({ text: t.dateiGeladen, fehler: false })
    } catch (fehler) {
      notiere(`Medien: ZIP nicht gesichert nach ${((performance.now() - started) / 1000).toFixed(1)} s: ${fehlertext(fehler)}`)
      setHinweis({ text: t.dateiNichtGeholt, fehler: true })
    } finally {
      setHolt(false)
    }
  }

  async function start() {
    const started = performance.now()
    try {
      const result = await build.mutateAsync()
      notiere(`Medien: ${result.files} Dateien gepackt, ${groesse(result.bytes)}, ${result.lookups} Ortsanfragen, in ${((performance.now() - started) / 1000).toFixed(1)} s`)
      setBuilt(result)
      setHinweis(null)
    } catch (fehler) {
      notiere(`Medien: fehlgeschlagen nach ${((performance.now() - started) / 1000).toFixed(1)} s: ${fehlertext(fehler)}`)
    }
  }

  return (
    <span className="exportknoepfe" data-export="medien-bereich">
      {build.isError && (
        <span className="notice notice--knapp" role="alert">
          {fehlertext(build.error)}
        </span>
      )}
      {built !== null && (
        <>
          <button
            type="button"
            className="button button--small"
            disabled={holt}
            data-export="medien-sichern"
            onClick={() => void sichern(built)}
          >
            {holt ? t.medienHolen(groesse(built.bytes)) : t.medienSichern}
          </button>
          {hinweis !== null && (
            <span className="exportknoepfe__hinweis" data-export="medien-hinweis" {...(hinweis.fehler ? { role: 'alert' } : {})}>
              {hinweis.text}
            </span>
          )}
          <span className="exportknoepfe__hinweis">
            <a href={`${built.url}?name=${encodeURIComponent(built.filename)}`} target="_blank" rel="noopener" data-export="medien-link">
              {t.medienOeffnen(built.filename, built.files, groesse(built.bytes))}
            </a>
            {alsApp() && ` ${t.appHinweis}`}
          </span>
        </>
      )}
      <button type="button" className="button button--small button--outline" onClick={() => void start()} disabled={build.isPending} data-export="medien">
        {build.isPending ? t.medienPackt : t.medienZip}
      </button>
      <span className="exportknoepfe__hinweis">{ortsnamen ? t.medienOrteApple : t.medienOrteOhne}</span>
    </span>
  )
}
